import { useState } from "react";
import {
  Caption1,
  Dropdown,
  Field,
  Link,
  MessageBar,
  MessageBarBody,
  Option,
  OptionGroup,
  Spinner,
  Switch,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Call16Regular, Mail16Regular, Person16Regular } from "@fluentui/react-icons";
import { useData } from "../data/DataProvider";
import { ambito, fornitoriPer, useFornitori, type FornitoreScelta } from "../data/fornitori";
import type { Richiesta } from "../data/richieste";

const useStyles = makeStyles({
  wrap: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  dropdown: { width: "100%", minWidth: 0 },
  option: { display: "flex", flexDirection: "column" },
  muted: { color: tokens.colorNeutralForeground3 },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  contact: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, overflowWrap: "anywhere" },
});

/** Cellulare leggibile: 331 789 7570, 376 449332. */
const formatTel = (t: string) => (/^3\d{8,9}$/.test(t) ? `${t.slice(0, 3)} ${t.slice(3, 6)} ${t.slice(6)}` : t);

/**
 * Scelta del fornitore (solo gestore). In cima i fornitori indicati per categoria,
 * sede e servizio della richiesta; gli altri con "Mostra tutti i fornitori".
 */
export function FornitoreField({ r, value, onChange }: { r: Richiesta; value?: number; onChange: (id: number | undefined) => void }) {
  const s = useStyles();
  const data = useData();
  const { fornitori, error } = useFornitori(data);
  const [tutti, setTutti] = useState(false);

  if (error)
    return (
      <MessageBar intent="warning">
        <MessageBarBody>Elenco fornitori non disponibile: puoi comunque salvare il resto.</MessageBarBody>
      </MessageBar>
    );
  if (!fornitori) return <Spinner size="tiny" label="Caricamento fornitori…" labelPosition="after" />;

  const { indicati, altri } = fornitoriPer(fornitori, r, value);
  const scelto = fornitori.find((x) => x.id === value);
  const altriVisibili = tutti ? altri : altri.filter((x) => x.id === value);
  const hint = indicati.length
    ? `${indicati.length} indicat${indicati.length === 1 ? "o" : "i"} per categoria, sede e servizio.`
    : "Nessun fornitore indicato per categoria, sede e servizio: attiva \"Mostra tutti i fornitori\".";

  const voce = (x: FornitoreScelta, dettaglio: boolean) => (
    <Option key={x.id} value={String(x.id)} text={x.nome}>
      <div className={s.option}>
        <span>
          {x.nome}
          {!x.attivo && " (non attivo)"}
        </span>
        {dettaglio && <Caption1 className={s.muted}>{ambito(x)}</Caption1>}
      </div>
    </Option>
  );

  return (
    <div className={s.wrap}>
      <Field label="Fornitore" hint={hint}>
        <Dropdown
          className={s.dropdown}
          size="large"
          placeholder="Nessun fornitore"
          value={scelto?.nome ?? ""}
          selectedOptions={value ? [String(value)] : []}
          onOptionSelect={(_, d) => onChange(d.optionValue ? Number(d.optionValue) : undefined)}
        >
          <Option value="" text="Nessun fornitore">
            <span className={s.muted}>Nessun fornitore</span>
          </Option>
          {indicati.length > 0 && <OptionGroup label="Indicati per questa richiesta">{indicati.map((x) => voce(x, false))}</OptionGroup>}
          {altriVisibili.length > 0 && <OptionGroup label="Altri fornitori">{altriVisibili.map((x) => voce(x, true))}</OptionGroup>}
        </Dropdown>
      </Field>
      <Switch
        checked={tutti}
        onChange={(_, d) => setTutti(d.checked)}
        label={`Mostra tutti i fornitori (${altri.length} altri)`}
      />
      {scelto && (scelto.referente || scelto.telefono || scelto.email || scelto.note) && (
        <div className={s.card}>
          {scelto.referente && (
            <span className={s.contact}>
              <Person16Regular /> {scelto.referente}
            </span>
          )}
          {scelto.telefono && (
            <span className={s.contact}>
              <Call16Regular /> <Link href={`tel:${scelto.telefono}`}>{formatTel(scelto.telefono)}</Link>
            </span>
          )}
          {scelto.email && (
            <span className={s.contact}>
              <Mail16Regular /> <Link href={`mailto:${scelto.email}`}>{scelto.email}</Link>
            </span>
          )}
          {scelto.note && <Caption1 className={s.muted}>{scelto.note}</Caption1>}
        </div>
      )}
    </div>
  );
}
